import type { ModulePayload } from '@/login/types/AccessTokenData';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { CustomerInformationsEntity, SystemSettingsEntity } from '@sysarb/sysarb-types-v2/general/types/legacyDatabase';
import type { BetaFlag, FeatureFlags, PasswordSettings, SetFeatureValue } from '@sysarb/sysarb-types/services/v1/authentication';
import { storeToRefs } from 'pinia';
import { computed, type ComputedRef } from 'vue';
import { useResgate } from '@/login/composables/useResgate';
import { instanceSettingsInputValidator, type InstanceSettings, type InstanceSettingsInput } from '@sysarb/sysarb-types-v2/services/v1/instance/instanceSettings';
import { EmploymentSalaryType } from '@sysarb/sysarb-types/general/enums/employmentSalaryType';
import { CustomizationInvolveViewIdentifier } from '@sysarb/sysarb-types/general/enums/customEmployment';
import type { CustomerSettings, UpdateCustomerSettingsInput } from '@sysarb/sysarb-types-v2/services/v1/job';
import type { ScimSettings } from '@sysarb/sysarb-types/services/v1/sso/scim';
import { useLabsStore } from '@/views/jobArchitecture/composables/useLabs';
import { useTracking } from '@/composables/useTracking';
import { TrackingEvent } from '@/tracking/events';

type CustomizationSettings = Pick<InstanceSettings, 'customizationEmploymentViewIdentifier' | 'customizationTranslationIdentifier'>;
export type InvolveSettings = Pick<InstanceSettings,
    'involveShowPayRangeInfo'
    | 'involveShowJobDescription'
    | 'involveShowJobProfileAveragePayLevels'
    | 'involveShowJobProfileAveragePayLevelsTotalSalary'
    | 'involveShowJobProfileAveragePayLevelsBaseSalary'
    | 'involveShowGradeAveragePayLevels'
    | 'involveShowGradeAveragePayLevelsTotalSalary'
    | 'involveShowGradeAveragePayLevelsBaseSalary'
    | 'involveShowPayQuartileGenderDistribution'
    | 'involveShowPayQuartileGenderDistributionTotalSalary'
    | 'involveShowPayQuartileGenderDistributionBaseSalary'
    | 'involveShowAveragePayGapsTotalSalary'
    | 'involveShowAveragePayGapsTotalSalaryFigures'
    | 'involveShowAveragePayGapsBaseSalary'
    | 'involveShowAveragePayGapsVariablePay'
    | 'involveShowAveragePayGapsVariablePayTotalSalary'
    | 'involveShowMonthlyTotalSalarySection'
    | 'involveShowCareerSection'
    | 'involveShowCareerSectionTotalSalary'
    | 'involveShowCareerSectionBaseSalary'
    | 'involveShowPerformanceSection'
    | 'involveShowTargetsSection'
>;

type ScimSettingAdditional = {
    canCreateUsers: boolean;
    canCreateRoles: boolean;
};

export type SettingReturn = {
    /**
     * Instance settings.
     */
    instanceSettings: ComputedRef<SystemSettingsEntity | null>;
    /**
     * Customer information.
     */
    customerInformation: ComputedRef<CustomerInformationsEntity | null>;
    /**
     * Security settings, like password requirements.
     */
    securitySettings: ComputedRef<PasswordSettings | null>;
    /**
     * Feature flags.
     */
    features: ComputedRef<FeatureFlags | null>;
    /**
     * Modules.
     */
    modules: ComputedRef<ModulePayload | null>;
    /**
     * Beta flags.
     */
    flags: ComputedRef<BetaFlag[]>;
    /**
     * System default salary type.
     */
    systemDefaultSalaryType: ComputedRef<EmploymentSalaryType | null>;
    /**
     * Job architecture settings.
     */
    jobArchitectureSettings: ComputedRef<CustomerSettings | null>;
    setJobArchitectureSettings: (input: UpdateCustomerSettingsInput) => Promise<boolean>;
    /**
     * Get & set involve settings.
     */
    involveSettings: ComputedRef<InvolveSettings | null>;
    setInvolveSettings: (payload: InstanceSettingsInput) => Promise<void>;

    customizationSettings: ComputedRef<CustomizationSettings | null>;
    hidesInvolveSettingsPage: ComputedRef<boolean>;

    setInstanceSetting: (name: string, value: AllowedSettingsValues) => Promise<boolean>;
    setSecuritySetting: (name: string, value: AllowedSettingsValues) => Promise<boolean>;
    setCustomerInformation: (name: string, value: AllowedSettingsValues) => Promise<boolean>;
    setFeature: (name: string, value: boolean) => Promise<boolean>;
    /**
     * Update default language on the current instance.
     */
    setInstanceLanguage: (language: string) => Promise<void>;
    scimSettings: ComputedRef<ScimSettings & ScimSettingAdditional | null>;
    setScimSettings: (payload: Pick<ScimSettings, 'roleProvisioningByScim'>) => Promise<void>;
    /**
     * Used to signal if any of the system critical settings are null.
     */
    instanceSettingsMissing: ComputedRef<boolean>;
};

export type AllowedSettingsValues = string | Date | number | boolean | null | undefined;

export function useSettings(): SettingReturn {
    const loginStore = useLoginStore();
    const { selectedInstance, userData, userId, instanceTopic } = storeToRefs(loginStore);
    const { activeFlags } = storeToRefs(useLabsStore());

    const setJobArchitectureSettings = async (input: UpdateCustomerSettingsInput): Promise<boolean> => {
        const resgate = useResgate(crypto.randomUUID());
        const result = await resgate.call(`v1.${instanceTopic.value}.job-architecture-settings`, 'set', input).then(r => {
            resgate.unsubscribe();
            return r;
        });
        return result.success;
    };

    const instanceSettings = computed(() => {
        if (selectedInstance.value == null) return null;
        return selectedInstance.value.settings;
    });

    const customerInformation = computed(() => {
        if (selectedInstance.value == null) return null;
        return selectedInstance.value.customer;
    });

    const securitySettings = computed(() => {
        if (selectedInstance.value == null) return null;
        return selectedInstance.value.security;
    });

    const features = computed(() => {
        if (selectedInstance.value == null) return null;
        return selectedInstance.value.features;
    });

    const modules = computed(() => {
        if (userData.value == null) return null;
        return userData.value.modules;
    });

    const flags = computed(() => {
        if (selectedInstance.value?.flags?.flags == null) return [];
        return selectedInstance.value.flags.flags;
    });

    const setFeature = async (name: string, value: boolean): Promise<boolean> => {
        if (features.value == null) return false;

        const data: SetFeatureValue = {
            name: name as keyof FeatureFlags,
            enabled: value
        };

        const resgate = useResgate(crypto.randomUUID());
        await resgate.call<void>('v1.client.features', 'set', data).then(() => {
            resgate.unsubscribe();
        });

        return true;
    };

    const involveSettings = computed<InvolveSettings | null>(() => {
        if (selectedInstance.value?.involveSettings == null) return null;

        return {
            involveShowPayRangeInfo: selectedInstance.value.involveSettings.involveShowPayRangeInfo,
            involveShowJobDescription: selectedInstance.value.involveSettings.involveShowJobDescription,
            involveShowJobProfileAveragePayLevels: selectedInstance.value.involveSettings.involveShowJobProfileAveragePayLevels,
            involveShowJobProfileAveragePayLevelsTotalSalary: selectedInstance.value.involveSettings.involveShowJobProfileAveragePayLevelsTotalSalary,
            involveShowJobProfileAveragePayLevelsBaseSalary: selectedInstance.value.involveSettings.involveShowJobProfileAveragePayLevelsBaseSalary,
            involveShowGradeAveragePayLevels: selectedInstance.value.involveSettings.involveShowGradeAveragePayLevels,
            involveShowGradeAveragePayLevelsTotalSalary: selectedInstance.value.involveSettings.involveShowGradeAveragePayLevelsTotalSalary,
            involveShowGradeAveragePayLevelsBaseSalary: selectedInstance.value.involveSettings.involveShowGradeAveragePayLevelsBaseSalary,
            involveShowPayQuartileGenderDistribution: selectedInstance.value.involveSettings.involveShowPayQuartileGenderDistribution,
            involveShowPayQuartileGenderDistributionTotalSalary: selectedInstance.value.involveSettings.involveShowPayQuartileGenderDistributionTotalSalary,
            involveShowPayQuartileGenderDistributionBaseSalary: selectedInstance.value.involveSettings.involveShowPayQuartileGenderDistributionBaseSalary,
            involveShowAveragePayGapsTotalSalary: selectedInstance.value.involveSettings.involveShowAveragePayGapsTotalSalary,
            involveShowAveragePayGapsTotalSalaryFigures: selectedInstance.value.involveSettings.involveShowAveragePayGapsTotalSalaryFigures,
            involveShowAveragePayGapsBaseSalary: selectedInstance.value.involveSettings.involveShowAveragePayGapsBaseSalary,
            involveShowAveragePayGapsVariablePay: selectedInstance.value.involveSettings.involveShowAveragePayGapsVariablePay,
            involveShowAveragePayGapsVariablePayTotalSalary: selectedInstance.value.involveSettings.involveShowAveragePayGapsVariablePayTotalSalary,
            involveShowMonthlyTotalSalarySection: selectedInstance.value.involveSettings.involveShowMonthlyTotalSalarySection,
            involveShowCareerSection: selectedInstance.value.involveSettings.involveShowCareerSection,
            involveShowCareerSectionTotalSalary: selectedInstance.value.involveSettings.involveShowCareerSectionTotalSalary,
            involveShowCareerSectionBaseSalary: selectedInstance.value.involveSettings.involveShowCareerSectionBaseSalary,
            involveShowPerformanceSection: selectedInstance.value.involveSettings.involveShowPerformanceSection,
            involveShowTargetsSection: selectedInstance.value.involveSettings.involveShowTargetsSection,
        };
    });

    const customizationSettings = computed<CustomizationSettings | null>(() => {
        if (selectedInstance.value?.involveSettings == null) return null;

        return {
            customizationTranslationIdentifier: selectedInstance.value.involveSettings.customizationTranslationIdentifier,
            customizationEmploymentViewIdentifier: selectedInstance.value.involveSettings.customizationEmploymentViewIdentifier,
        };
    });

    const hidesInvolveSettingsPage = computed<boolean>(() => {
        const identifier = customizationSettings.value?.customizationEmploymentViewIdentifier;
        return identifier === CustomizationInvolveViewIdentifier.HIT_INVOLVE_001
            || identifier === CustomizationInvolveViewIdentifier.HIT_INVOLVE_DEAI_001;
    });

    /**
     * Used to signal if any of the system critical settings are null.
     */
    const instanceSettingsMissing = computed(() => {
        if (selectedInstance.value == null) return false;

        if (selectedInstance.value.involveSettings == null) return true;
        if (selectedInstance.value.jobArchitectureSettings == null) return true;
        if (selectedInstance.value.scimSettings == null) return true;
        if (selectedInstance.value.flags == null) return true;
        if (selectedInstance.value.user == null) return true;
        if (selectedInstance.value.basics == null) return true;
        return false;
    });

    const setInstanceLanguage = async (language: string): Promise<void> => {
        const resgate = useResgate(crypto.randomUUID());
        await resgate.call<void>(`v2.access.token.${userId.value}.instance.${instanceTopic.value}`, 'setlanguage', {
            language
        }).then(() => {
            resgate.unsubscribe();
        });
    };

    const setInvolveSettings = async (payload: InstanceSettingsInput): Promise<void> => {
        const validatedPayload = instanceSettingsInputValidator.safeParse(payload);

        if (validatedPayload.success === true) {
            const resgate = useResgate(crypto.randomUUID());
            await resgate.call<InstanceSettings>(`v1.${instanceTopic.value}.system.instance.display-settings`, 'set', validatedPayload.data).then(() => {
                resgate.unsubscribe();
            });
        } else {
            console.error('Invalid payload', validatedPayload.error);
        }
    };

    const setInstanceSetting = async (name: string, value: AllowedSettingsValues): Promise<boolean> => {
        if (instanceSettings.value == null) return false;
        if (!(name in instanceSettings.value)) {
            console.warn('Trying to change non-existing instance setting', name);
            return false;
        }

        const resgate = useResgate(crypto.randomUUID());
        // call() resolves { success: false } on a Resgate error instead of rejecting, so pass that on
        const result = await resgate.call<void>(`v2.access.token.${userId.value}.instance.${instanceTopic.value}.settings`, 'set', {
            [name]: value
        });
        resgate.unsubscribe();

        return result.success;
    };

    const setSecuritySetting = async (name: string, value: AllowedSettingsValues): Promise<boolean> => {
        if (securitySettings.value == null) return false;
        if (!(name in securitySettings.value)) {
            console.warn('Trying to change non-existing security setting', name);
            return false;
        }

        const resgate = useResgate(crypto.randomUUID());
        await resgate.call<void>(`v2.access.token.${userId.value}.instance.${instanceTopic.value}.security`, 'set', {
            [name]: value
        }).then(() => {
            resgate.unsubscribe();
        });

        useTracking().track(TrackingEvent.ChangedSecuritySettings, { setting: name });

        return true;
    };

    const setCustomerInformation = async (name: string, value: AllowedSettingsValues): Promise<boolean> => {
        if (customerInformation.value == null) return false;
        if (!(name in customerInformation.value)) {
            console.warn('Trying to change non-existing customer information', name);
            return false;
        }

        const resgate = useResgate(crypto.randomUUID());
        await resgate.call<void>(`v2.access.token.${userId.value}.instance.${instanceTopic.value}.customer`, 'set', {
            [name]: value
        }).then(() => {
            resgate.unsubscribe();
        });

        return true;
    };

    const systemDefaultSalaryType = computed<EmploymentSalaryType | null>(() => {
        if (instanceSettings.value?.DefaultSalaryType == null) return null;

        switch (Number.parseInt(instanceSettings.value.DefaultSalaryType)) {
            case EmploymentSalaryType.hourly:
            case EmploymentSalaryType.hourlyTotalCompensation:
                return EmploymentSalaryType.hourly;
            case EmploymentSalaryType.monthly:
            case EmploymentSalaryType.monthlyTotalCompensation:
                return EmploymentSalaryType.monthly;
            case EmploymentSalaryType.yearly:
            case EmploymentSalaryType.yearlyTotalCompensation:
                return EmploymentSalaryType.yearly;
            default:
                return null;
        }
    });

    const jobArchitectureSettings = computed(() => {
        if (selectedInstance.value == null) return null;
        return selectedInstance.value.jobArchitectureSettings;
    });

    const scimSettings = computed(() => {
        if (selectedInstance.value == null) return null;

        if (selectedInstance.value.scimSettings == null) return null;

        const scimActive = activeFlags.value.includes('SCIM');

        return {
            ...selectedInstance.value.scimSettings,
            canCreateUsers: scimActive === false || selectedInstance.value.scimSettings.hasApiKey !== true,
            canCreateRoles: scimActive === false || selectedInstance.value.scimSettings.roleProvisioningByScim !== true
        };
    });

    const setScimSettings = async (payload: Pick<ScimSettings, 'roleProvisioningByScim'>): Promise<void> => {
        const resgate = useResgate(crypto.randomUUID());
        await resgate.call<void>(`v1.${instanceTopic.value}.sso.scim`, 'set', payload).then(() => {
            resgate.unsubscribe();
        });
    };

    return {
        instanceSettings,
        customerInformation,
        securitySettings,
        features,
        modules,
        flags,
        involveSettings,
        customizationSettings,
        hidesInvolveSettingsPage,
        setInvolveSettings,
        setInstanceSetting,
        setSecuritySetting,
        setCustomerInformation,
        setFeature,
        setInstanceLanguage,
        systemDefaultSalaryType,
        jobArchitectureSettings,
        setJobArchitectureSettings,
        scimSettings,
        setScimSettings,
        instanceSettingsMissing
    };
}
